/**
 * Game modal — fullscreen modal for daily stage tasks.
 *
 * Routes by task category:
 *   - quiz   → assets/js/quiz-gameplay.js
 *   - memory → assets/js/memory-game.js
 *
 * Triggered by clicks on .js-day-card where data-stage-id is non-empty and
 * the card is neither .is-done nor .s-stage-days__card--locked.
 */

import { renderQuiz, teardownQuiz, forceEndQuiz, WELCOME_CONFIG as QUIZ_WELCOME } from '../games/quiz/quiz-gameplay';
import { renderMemory, teardownMemory, forceEndMemory, WELCOME_CONFIG as MEMORY_WELCOME } from '../games/memory/memory-game';
import { renderPuzzle, teardownPuzzle, forceEndPuzzle, WELCOME_CONFIG as PUZZLE_WELCOME } from '../games/puzzle/puzzle-game';
import { renderFastChoice, teardownFastChoice, forceEndFastChoice, WELCOME_CONFIG as FAST_CHOICE_WELCOME } from '../games/fast-choice/fast-choice';
import { renderBlurredImage, teardownBlurredImage, forceEndBlurredImage, WELCOME_CONFIG as BLURRED_IMAGE_WELCOME } from '../games/blurred-image/blurred-image';
import { renderOnTheSpot, teardownOnTheSpot, forceEndOnTheSpot, WELCOME_CONFIG as ON_THE_SPOT_WELCOME } from '../games/on-the-spot/on-the-spot';
import { renderSpotDifference, teardownSpotDifference, forceEndSpotDifference, WELCOME_CONFIG as SPOT_DIFFERENCE_WELCOME } from '../games/spot-difference/spot-difference';
import { renderSephoRace, teardownSephoRace, pauseSephoRace, resumeSephoRace, forceEndSephoRace, WELCOME_CONFIG as SEPHO_RACE_WELCOME } from '../games/sepho-race/sepho-race';
import { renderDrop, teardownDrop, pauseDrop, resumeDrop, forceEndDrop, buildDropWelcome } from '../games/drop/drop';
import { renderSephoHills, teardownSephoHills, pauseSephoHills, resumeSephoHills, forceEndSephoHills, WELCOME_CONFIG as SEPHO_HILLS_WELCOME } from '../games/sepho-hills/sepho-hills';
import { renderSephoWave, teardownSephoWave, pauseSephoWave, resumeSephoWave, forceEndSephoWave, WELCOME_CONFIG as SEPHO_WAVE_WELCOME } from '../games/sepho-wave/sepho-wave';
import { fillWelcomeScreen } from './game-welcome';
import { go, isPastStage, replayDaysReveal, refreshDaysSnapshot } from './stage-transitions';
import { showCheckInReward } from './checkin-reward';

let currentStageCardEl   = null;
let currentCategory      = null;
let currentGameFinished  = false;
let currentDayNum        = null;
let pendingCheckInReward = null;

function modal()   { return document.getElementById('js-game-modal'); }
function content() { return modal()?.querySelector('.game-modal__content'); }

function showPause() {
    const m = modal();
    if (!m) return;
    if (m.querySelector('.game-modal__pause')) return;
    const overlay = document.createElement('div');
    overlay.className = 'game-modal__pause';
    overlay.innerHTML = `
        <div class="game-modal__pause-card">
            <h2 class="game-modal__pause-title">PAUZA</h2>
            <button type="button" class="game-modal__pause-btn game-modal__pause-btn--resume js-game-pause-resume">Wznów</button>
            <button type="button" class="game-modal__pause-btn game-modal__pause-btn--exit js-game-pause-exit">Zakończ grę</button>
        </div>
    `;
    m.querySelector('.game-modal__card')?.appendChild(overlay);
}

function hidePause() {
    modal()?.querySelector('.game-modal__pause')?.remove();
}

function showResultCard(redeemablePoints, rankingPoints) {
    const m = modal();
    if (!m) return;
    m.querySelectorAll('.game-result__points-redeemable').forEach(el => el.textContent = `+${redeemablePoints}`);
    m.querySelectorAll('.game-result__points-ranking').forEach(el => el.textContent = `+${rankingPoints}`);
    if (currentDayNum !== null) {
        m.querySelectorAll('.game-result__day-value').forEach(el => el.textContent = currentDayNum);
    }
    document.body.classList.remove('has-landscape-game');
    m.classList.toggle('has-past-stage-result', isPastStage());
    m.classList.add('has-result-open');

    // If this game's check-in crossed a streak threshold, celebrate it on top of the
    // result card. Delayed slightly so the result screen is seen first.
    if (pendingCheckInReward) {
        const reward = pendingCheckInReward;
        pendingCheckInReward = null;
        setTimeout(() => showCheckInReward(reward), 700);
    }
}

// ── Back-navigation trap ──────────────────────────────────────────────────────
// System back (Android button/gesture, iOS edge-swipe) would otherwise navigate
// the whole standalone PWA away from the game with no confirmation. We push a
// dummy history entry while the modal is open and convert the resulting popstate
// into the same pause/exit confirmation the close (X) button shows.
let backTrapArmed    = false;
let suppressPopstate = false;

function armBackTrap() {
    if (backTrapArmed) return;
    backTrapArmed = true;
    history.pushState({ gameModalTrap: true }, '', location.href);
    window.addEventListener('popstate', onModalPopstate);
}

function disarmBackTrap() {
    if (!backTrapArmed) return;
    backTrapArmed = false;
    window.removeEventListener('popstate', onModalPopstate);
    // Consume the single dummy entry we added so we don't leave a dead step in history.
    if (history.state && history.state.gameModalTrap) {
        suppressPopstate = true;
        history.back();
    }
}

function onModalPopstate() {
    if (suppressPopstate) { suppressPopstate = false; return; }
    // Re-arm immediately so a second back press is also caught.
    history.pushState({ gameModalTrap: true }, '', location.href);
    requestPauseOrClose();
}

// Same behaviour as tapping the modal close (X): pause + confirm mid-game, or close if done.
function requestPauseOrClose() {
    const isGame = currentCategory === 'memory' || currentCategory === 'quiz' || currentCategory === 'puzzle' || currentCategory === 'fast_choice' || currentCategory === 'blurred_image' || currentCategory === 'on_the_spot' || currentCategory === 'spot_difference' || currentCategory === 'sepho_race' || currentCategory === 'drop' || currentCategory === 'sepho_hills' || currentCategory === 'sepho_wave';
    if (isGame && !currentGameFinished) {
        showPause();
        if (currentCategory === 'drop') pauseDrop();
        if (currentCategory === 'sepho_race') pauseSephoRace();
        if (currentCategory === 'sepho_wave') pauseSephoWave();
        if (currentCategory === 'sepho_hills') pauseSephoHills();
    } else {
        const wasFinished = currentGameFinished;
        closeModal();
        if (wasFinished) replayDaysReveal();
    }
}

function openModal() {
    const m = modal();
    if (!m) return;
    m.classList.add('is-open');
    m.setAttribute('aria-hidden', 'false');
    document.body.classList.add('has-game-open');
    armBackTrap();
}

function closeModal() {
    const m = modal();
    if (!m) return;
    disarmBackTrap();
    hidePause();
    teardownActive(m);

    m.classList.remove('is-open');
    m.setAttribute('aria-hidden', 'true');
    m.classList.remove('has-result-open', 'has-past-stage-result');
    document.body.classList.remove('has-game-open', 'has-landscape-game');
    currentStageCardEl  = null;
    currentCategory     = null;
    currentGameFinished = false;
    currentDayNum       = null;
    pendingCheckInReward = null;
}

function teardownActive(m) {
    if (currentCategory === 'quiz')   teardownQuiz(m);
    if (currentCategory === 'memory') teardownMemory(m);
    if (currentCategory === 'puzzle') teardownPuzzle(m);
    if (currentCategory === 'fast_choice') teardownFastChoice(m);
    if (currentCategory === 'blurred_image') teardownBlurredImage(m);
    if (currentCategory === 'on_the_spot') teardownOnTheSpot(m);
    if (currentCategory === 'spot_difference') teardownSpotDifference(m);
    if (currentCategory === 'sepho_race') teardownSephoRace(m);
    if (currentCategory === 'drop') teardownDrop(m);
    if (currentCategory === 'sepho_hills') teardownSephoHills(m);
    if (currentCategory === 'sepho_wave') teardownSephoWave(m);
}

function markCardDone(result) {
    // Result endpoints report the check-in outcome read from the GameLog. Capture a
    // crossed-threshold reward so the pop-up can fire once the result card is shown.
    if (result?.checkIn?.thresholdPassed && result.checkIn.reward) {
        pendingCheckInReward = result.checkIn.reward;
    }
    if (currentStageCardEl) {
        currentStageCardEl.classList.add('is-done', 'is-completed-visible');
        // The decor is a sibling of the card, not a child, so .is-completed-visible
        // can't reveal it. Add .is-done to the matching decor (always in the DOM
        // now) so it appears too — must run before refreshDaysSnapshot() below so
        // the snapshot/replay round-trip keeps it visible.
        const dayNum = currentStageCardEl.dataset.dayNum;
        currentStageCardEl.closest('.js-days-cards')
            ?.querySelector(`.js-day-decor[data-day-num="${dayNum}"]`)
            ?.classList.add('is-done');
    }
    if (result) {
        // Persist the completion into the days snapshot so the stamp round-trip
        // (swapDaysCards restore) keeps the card .is-done without a page refresh.
        refreshDaysSnapshot();
        currentGameFinished = true;
        if (currentDayNum) {
            const slots = document.querySelectorAll('.s-game__progress-pill .s-game__slot');
            const slot = slots[parseInt(currentDayNum, 10) - 1];
            if (slot) slot.classList.add('s-game__slot--filled');
        }
    }
    const earned = result?.totalRankingPoints ?? result?.rankingPointsEarned ?? 0;
    if (typeof earned === 'number' && earned > 0) {
        const hud = document.querySelector('.s-game__pts-box--ranking .s-game__pts-value');
        if (hud) {
            const current = parseInt(hud.textContent, 10) || 0;
            hud.textContent = String(current + earned);
        }
    }
    const redeemEarned = result?.totalRedeemablePoints ?? result?.redeemablePointsEarned ?? 0;
    if (typeof redeemEarned === 'number' && redeemEarned > 0) {
        const hud = document.querySelector('.s-game__pts-box--reward .s-game__pts-value');
        if (hud) {
            const current = parseInt(hud.textContent, 10) || 0;
            hud.textContent = String(current + redeemEarned);
        }
    }
}

async function openForStage(stageId, cardEl) {
    try {
        const res  = await fetch(`/api/task/by-stage/${stageId}`);
        const data = await res.json();

        if (!res.ok || !data || data.completed) return;

        currentStageCardEl = cardEl;
        currentCategory    = data.category;
        currentDayNum      = cardEl.dataset.dayNum ?? null;
        pendingCheckInReward = null;

        const m = modal();
        if (!m) return;

        content().innerHTML = '';

        if (data.category === 'quiz') {
            if (!data.quiz?.questions?.length) return;
            fillWelcomeScreen(QUIZ_WELCOME, () => {
                go('days');
                openModal();
                markCardDone();
                renderQuiz(m, data, markCardDone);
            });
            go('welcome');
        } else if (data.category === 'memory') {
            if (!data.memoryGame?.config) return;
            fillWelcomeScreen(MEMORY_WELCOME, () => {
                go('days');
                openModal();
                markCardDone();
                renderMemory(m, data, markCardDone);
            });
            go('welcome');
        } else if (data.category === 'puzzle') {
            if (!data.puzzleGame?.config) return;
            fillWelcomeScreen(PUZZLE_WELCOME, () => {
                go('days');
                openModal();
                renderPuzzle(m, data, markCardDone);
            });
            go('welcome');
        } else if (data.category === 'fast_choice') {
            if (!data.fastChoiceGame?.config) return;
            fillWelcomeScreen(FAST_CHOICE_WELCOME, () => {
                go('days');
                openModal();
                renderFastChoice(m, data, markCardDone);
            });
            go('welcome');
        } else if (data.category === 'blurred_image') {
            if (!data.blurredImageGame?.config) return;
            fillWelcomeScreen(BLURRED_IMAGE_WELCOME, () => {
                go('days');
                openModal();
                renderBlurredImage(m, data, markCardDone);
            });
            go('welcome');
        } else if (data.category === 'on_the_spot') {
            if (!data.onTheSpotGame?.config) return;
            fillWelcomeScreen(ON_THE_SPOT_WELCOME, () => {
                go('days');
                openModal();
                renderOnTheSpot(m, data, markCardDone);
            });
            go('welcome');
        } else if (data.category === 'spot_difference') {
            if (!data.spotDifferenceGame?.config) return;
            fillWelcomeScreen(SPOT_DIFFERENCE_WELCOME, () => {
                go('days');
                document.body.classList.add('has-landscape-game');
                openModal();
                renderSpotDifference(m, data, markCardDone);
            });
            go('welcome');
        } else if (data.category === 'sepho_race') {
            if (!data.sephoRaceGame?.config) return;
            fillWelcomeScreen(SEPHO_RACE_WELCOME, () => {
                go('days');
                openModal();
                renderSephoRace(m, data, markCardDone);
            });
            go('welcome');
        } else if (data.category === 'drop') {
            if (!data.dropGame?.config) return;
            fillWelcomeScreen(buildDropWelcome(data), () => {
                go('days');
                openModal();
                renderDrop(m, data, markCardDone);
            });
            go('welcome');
        } else if (data.category === 'sepho_hills') {
            if (!data.sephoHillsGame?.config) return;
            fillWelcomeScreen(SEPHO_HILLS_WELCOME, () => {
                go('days');
                document.body.classList.add('has-landscape-game');
                openModal();
                renderSephoHills(m, data, markCardDone);
            });
            go('welcome');
        } else if (data.category === 'sepho_wave') {
            if (!data.sephoWaveGame?.config) return;
            fillWelcomeScreen(SEPHO_WAVE_WELCOME, () => {
                go('days');
                document.body.classList.add('has-landscape-game');
                openModal();
                renderSephoWave(m, data, markCardDone);
            });
            go('welcome');
        }
    } catch {
        // network error — ignore silently
    }
}

document.addEventListener('click', (e) => {
    const card = e.target.closest('.js-day-card');
    if (card) {
        const stageId  = card.dataset.stageId;
        const isLocked = card.classList.contains('s-stage-days__card--locked');
        const isDone   = card.classList.contains('is-done');

        if (!stageId || isLocked) return;

        if (isDone) {
            card.classList.add('is-shake');
            card.addEventListener('animationend', () => card.classList.remove('is-shake'), { once: true });
            window.alert('Ta gra została już rozegrana', 'info');
            return;
        }

        openForStage(stageId, card);
        return;
    }

    if (e.target.closest('.js-game-modal-close')) {
        requestPauseOrClose();
        return;
    }

    if (e.target.closest('.js-game-pause-resume')) {
        hidePause();
        if (currentCategory === 'drop') resumeDrop();
        if (currentCategory === 'sepho_race') resumeSephoRace();
        if (currentCategory === 'sepho_wave') resumeSephoWave();
        if (currentCategory === 'sepho_hills') resumeSephoHills();
        return;
    }

    if (e.target.closest('.js-game-pause-exit')) {
        hidePause();
        if (currentCategory === 'quiz') {
            forceEndQuiz();
            return;
        }
        if (currentCategory === 'fast_choice') {
            forceEndFastChoice();
            return;
        }
        if (currentCategory === 'on_the_spot') {
            forceEndOnTheSpot();
            return;
        }
        if (currentCategory === 'drop') {
            forceEndDrop();
            return;
        }
        if (currentCategory === 'spot_difference') {
            forceEndSpotDifference();
            return;
        }
        if (currentCategory === 'sepho_race') {
            forceEndSephoRace();
            return;
        }
        if (currentCategory === 'memory') {
            forceEndMemory();
            return;
        }
        if (currentCategory === 'puzzle') {
            forceEndPuzzle();
            return;
        }
        if (currentCategory === 'blurred_image') {
            forceEndBlurredImage();
            return;
        }
        if (currentCategory === 'sepho_hills') {
            forceEndSephoHills();
            return;
        }
        if (currentCategory === 'sepho_wave') {
            forceEndSephoWave();
            return;
        }
        markCardDone();
        closeModal();
        return;
    }

    if (e.target.closest('.js-game-close-results')) {
        const wasFinished = currentGameFinished;
        closeModal();
        if (wasFinished) replayDaysReveal();
        return;
    }
});

document.addEventListener('game-modal:request-close', () => closeModal());
document.addEventListener('game-modal:result-show', (e) => showResultCard(e.detail.redeemablePoints, e.detail.rankingPoints));
